import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Users, Trophy, FileText, Sparkles, Heart, Star, Palette, Home } from 'lucide-react';

// Import components
import { LessonPlan } from '@/components/LessonPlan';
import { StorySection } from '@/components/StorySection';
import { GrammarExercises } from '@/components/GrammarExercises';
import { CompetitiveQuiz } from '@/components/CompetitiveQuiz';
import { VocabularyFlashcards } from '@/components/VocabularyFlashcards';
import { HomeworkRecording } from '@/components/HomeworkRecording';

const Index = () => {
  const [activeTab, setActiveTab] = useState('lesson-plan');
  const [isFrench, setIsFrench] = useState(false);

  // French translations
  const translations = {
    title: {
      en: "Betty's Magical Friendship Adventure",
      fr: "L'Aventure Magique d'Amitié de Betty"
    },
    subtitle: {
      en: "Interactive A1/A2 English Learning for Ages 6-8",
      fr: "Apprentissage Interactif A1/A2 Anglais pour 6-8 ans"
    },
    mainTitle: {
      en: "Join Betty's Magical Adventure!",
      fr: "Rejoins l'Aventure Magique de Betty !"
    },
    mainDescription: {
      en: "Discover a magical garden with Betty, meet Luna the unicorn and Spark the dragon, and learn English through friendship, colors, numbers, and fun activities!",
      fr: "Découvre un jardin magique avec Betty, rencontre Luna la licorne et Spark le dragon, et apprends l'anglais à travers l'amitié, les couleurs, les nombres et des activités amusantes !"
    },
    tabs: {
      lessonPlan: { en: "Lesson Plan", fr: "Plan de Cours" },
      story: { en: "Betty's Story", fr: "Histoire de Betty" },
      practice: { en: "Practice Games", fr: "Jeux d'Entraînement" },
      quiz: { en: "Friendship Quiz", fr: "Quiz d'Amitié" },
      homework: { en: "Magic Homework", fr: "Devoirs Magiques" }
    },
    storyFeatures: {
      colors: { en: "Learn 8 beautiful colors", fr: "Apprends 8 belles couleurs" },
      numbers: { en: "Count from 1 to 5", fr: "Compte de 1 à 5" },
      friends: { en: "Meet unicorns and dragons", fr: "Rencontre licornes et dragons" },
      friendship: { en: "Learn about helping friends", fr: "Apprends à aider les amis" }
    },
    practiceFeatures: {
      grammar: { en: "Fun exercises with colors and numbers", fr: "Exercices amusants avec couleurs et nombres" },
      flashcards: { en: "Interactive flashcards with audio", fr: "Cartes interactives avec audio" }
    },
    quizFeatures: {
      twoPlayers: { en: "Compete with a friend", fr: "Concours avec un ami" },
      timed: { en: "15 seconds per question", fr: "15 secondes par question" }
    },
    homeworkFeatures: {
      drawing: { en: "Color and draw magical scenes", fr: "Colorie et dessine des scènes magiques" },
      speaking: { en: "Record yourself speaking English", fr: "Enregistre-toi parlant anglais" },
      writing: { en: "Create your own magical adventure", fr: "Crée ta propre aventure magique" }
    },
    footer: {
      en: "✨ Magical English Learning • Ages 6-8 • A1/A2 Level • Interactive Fun ✨",
      fr: "✨ Apprentissage Magique de l'Anglais • 6-8 ans • Niveau A1/A2 • Amusement Interactif ✨"
    }
  };

  const t = (key: any) => {
    const keys = key.split('.');
    let value = translations;
    for (const k of keys) {
      value = value[k];
    }
    return value ? (isFrench ? value.fr : value.en) : key;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b-4 border-purple-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{t('title')}</h1>
                <p className="text-sm text-gray-600">{t('subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <Button
                variant="outline"
                onClick={() => setIsFrench(!isFrench)}
                className="bg-gradient-to-r from-blue-50 to-red-50 hover:from-blue-100 hover:to-red-100 border-2 border-blue-200 hover:border-blue-300 transition-all duration-300"
              >
                <span className="text-lg mr-2">{isFrench ? '🇬🇧' : '🇫🇷'}</span>
                <span className="font-medium">
                  {isFrench ? 'English' : 'Français'}
                </span>
              </Button>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{isFrench ? 'Niveau:' : 'Level:'}</span>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">A1/A2</span>
                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">{isFrench ? '6-8 ans' : 'Ages 6-8'}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-purple-800 mb-4 flex items-center justify-center gap-2">
            <Heart className="w-8 h-8 text-pink-500" />
            {t('mainTitle')}
            <Heart className="w-8 h-8 text-pink-500" />
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('mainDescription')}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-8 bg-white border-2 border-purple-200">
            <TabsTrigger value="lesson-plan" className="flex items-center gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
              <FileText className="w-4 h-4" />
              {t('tabs.lessonPlan')}
            </TabsTrigger>
            <TabsTrigger value="story" className="flex items-center gap-2 data-[state=active]:bg-pink-100 data-[state=active]:text-pink-700">
              <BookOpen className="w-4 h-4" />
              {t('tabs.story')}
            </TabsTrigger>
            <TabsTrigger value="practice" className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              <Star className="w-4 h-4" />
              {t('tabs.practice')}
            </TabsTrigger>
            <TabsTrigger value="quiz" className="flex items-center gap-2 data-[state=active]:bg-green-100 data-[state=active]:text-green-700">
              <Trophy className="w-4 h-4" />
              {t('tabs.quiz')}
            </TabsTrigger>
            <TabsTrigger value="homework" className="flex items-center gap-2 data-[state=active]:bg-orange-100 data-[state=active]:text-orange-700">
              <Home className="w-4 h-4" />
              {t('tabs.homework')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lesson-plan" className="space-y-6">
            <LessonPlan />
          </TabsContent>

          <TabsContent value="story" className="space-y-6">
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="text-center text-purple-600 flex items-center justify-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  {t('title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4 mb-6 text-center">
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-700">🌈 {isFrench ? 'Couleurs' : 'Colors'}</h4>
                    <p className="text-sm text-purple-600">{t('storyFeatures.colors')}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-700">🔢 {isFrench ? 'Nombres' : 'Numbers'}</h4>
                    <p className="text-sm text-blue-600">{t('storyFeatures.numbers')}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-700">🦄 {isFrench ? 'Amis Magiques' : 'Magic Friends'}</h4>
                    <p className="text-sm text-green-600">{t('storyFeatures.friends')}</p>
                  </div>
                  <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
                    <h4 className="font-semibold text-pink-700">👫 {isFrench ? 'Amitié' : 'Friendship'}</h4>
                    <p className="text-sm text-pink-600">{t('storyFeatures.friendship')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <StorySection isFrench={isFrench} />
          </TabsContent>

          <TabsContent value="practice" className="space-y-6">
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="text-center text-blue-600 flex items-center justify-center gap-2">
                  <Star className="w-5 h-5" />
                  {isFrench ? 'Jeux d\'Apprentissage Magiques' : 'Magic Learning Games'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4 mb-6 text-center">
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-700">🎮 {isFrench ? 'Jeux de Grammaire' : 'Grammar Games'}</h4>
                    <p className="text-sm text-yellow-600">{t('practiceFeatures.grammar')}</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-700">🃏 Magic Word Cards</h4>
                    <p className="text-sm text-orange-600">{t('practiceFeatures.flashcards')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Tabs defaultValue="grammar" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="grammar">{isFrench ? 'Jeux de Grammaire' : 'Grammar Games'}</TabsTrigger>
                <TabsTrigger value="flashcards">{isFrench ? 'Cartes de Mots' : 'Magic Word Cards'}</TabsTrigger>
              </TabsList>
              
              <TabsContent value="grammar">
                <GrammarExercises isFrench={isFrench} />
              </TabsContent>
              
              <TabsContent value="flashcards">
                <VocabularyFlashcards isFrench={isFrench} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="quiz" className="space-y-6">
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-center text-green-600 flex items-center justify-center gap-2">
                  <Trophy className="w-5 h-5" />
                  {isFrench ? 'Défi Quiz d\'Amitié de Betty' : 'Betty\'s Friendship Quiz Challenge'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4 mb-6 text-center">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-700">👫 {isFrench ? 'Deux Joueurs' : 'Two Players'}</h4>
                    <p className="text-sm text-green-600">{t('quizFeatures.twoPlayers')}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-700">⏰ {isFrench ? 'Questions Chronométrées' : 'Timed Questions'}</h4>
                    <p className="text-sm text-blue-600">{t('quizFeatures.timed')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <CompetitiveQuiz isFrench={isFrench} />
          </TabsContent>

          <TabsContent value="homework" className="space-y-6">
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-orange-200">
              <CardHeader>
                <CardTitle className="text-center text-orange-600 flex items-center justify-center gap-2">
                  <Palette className="w-5 h-5" />
                  {isFrench ? 'Activités de Devoirs Magiques de Betty' : 'Betty\'s Magic Homework Activities'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4 mb-6 text-center">
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-700">🎨 {isFrench ? 'Dessin Amusant' : 'Drawing Fun'}</h4>
                    <p className="text-sm text-yellow-600">{t('homeworkFeatures.drawing')}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-700">🎤 {isFrench ? 'Pratique Orale' : 'Speaking Practice'}</h4>
                    <p className="text-sm text-green-600">{t('homeworkFeatures.speaking')}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-700">📝 {isFrench ? 'Écriture d\'Histoire' : 'Story Writing'}</h4>
                    <p className="text-sm text-blue-600">{t('homeworkFeatures.writing')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <HomeworkRecording isFrench={isFrench} />
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <footer className="mt-12 text-center text-gray-600">
          <div className="border-t border-purple-200 pt-6">
            <p className="text-sm flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              {t('footer')}
              <Sparkles className="w-4 h-4 text-purple-500" />
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Index;